/**
 * SIMS-8876 — keep a program's inactive payer configured.
 *
 * THE DEFECT (verified on origin/release): `Programs/index.tsx:568` rebuilt the visible selection
 * as `payersData.filter(p => program.payers.includes(p._id))`, where `payersData` comes from
 * `useActivePayers` and therefore holds ACTIVE payers only. A saved-but-inactive payer was filtered
 * out of the display, and `:2155` then rewrote the saved list from that truncated state
 * (`payers: newValue.map(p => p._id)`) — so the payer was lost permanently the first time anyone
 * touched the field for any reason. Nobody was told.
 *
 * THE FIX IS HERE, NOT IN `useActivePayers`. That hook is correct for its other six consumers
 * (Participants/Profile, Beneficiary/Profile, SelectPayerPlan, VisitDetailTab,
 * ProgramActivitySummaryTab, ManagePayerPlans). Broadening it would leak inactive payers into
 * registration and reporting — a larger bug than this one.
 *
 * The rule, from the design precedent the ticket names (SIMS-8831 AC3;
 * `Cups & Analytes Pattern v2.dc.html:765`): "An inactive cup stays listed and selected on plans
 * that already use it. It cannot be added to a new plan." So: resolve what is CONFIGURED against
 * every payer, and offer for NEW selection only what is active — plus whatever is already applied.
 *
 * Pure functions on purpose: the Programs page is 2393 lines, and the defect is worth asserting
 * directly rather than through a mount of everything else on that screen.
 */

/** The shape both functions need. Deliberately minimal so callers can pass their own payer type. */
export interface RetainablePayer {
	_id: string;
	status: string;
}

const ACTIVE_STATUS = "ACTIVE";

/**
 * Resolve a program's saved payer ids into payer objects for the field's `value`.
 *
 * Resolves against `allPayers` rather than `activePayers` — that single difference is the fix.
 * Order follows the SAVED ids: the field records what was configured, and reordering it on open
 * would be a change the administrator never made.
 *
 * An id matching no payer at all is dropped: a deleted payer is genuinely gone, unlike an inactive
 * one, so there is nothing to show and nothing to preserve.
 *
 * `allPayers` may still be empty while its query resolves, and this function returns only the
 * subset it CAN resolve.
 *
 * WARNING — that is NOT currently safe, and an earlier version of this comment wrongly claimed it
 * was ("the field's value is recomputed when the data arrives"). Nothing recomputes it: Programs
 * has no useEffect, and `selectedPayers` is written only on reset, once inside `handleEditProgram`,
 * and on a user edit. If `usePayers()` has not resolved when Edit is opened, an inactive payer is
 * missing from the snapshot, and the next user edit writes that truncated list back — re-arming
 * the exact defect SIMS-8876 fixed. `useActivePayers` resolving first does not help: it is a
 * separate query key, and the default QueryClient sets no staleTime, so both refetch on mount.
 *
 * The caller must therefore not open the editor until the full payer list has resolved, or must
 * recompute when it does. Tracked as a follow-up.
 */
export function resolveConfiguredPayers<T extends RetainablePayer>(
	savedPayerIds: string[] | undefined | null,
	activePayers: T[],
	allPayers: T[],
): T[] {
	const saved = savedPayerIds ?? [];
	if (saved.length === 0) {
		return [];
	}
	// Prefer the full list; fall back to the active one for the window where only it has loaded.
	const byId = new Map<string, T>();
	activePayers.forEach((payer) => byId.set(payer._id, payer));
	allPayers.forEach((payer) => byId.set(payer._id, payer));

	return saved
		.map((id) => byId.get(id))
		.filter((payer): payer is T => payer !== undefined);
}

/**
 * The options a program's payer field may offer.
 *
 * AC3: an inactive payer cannot be NEWLY added, but one already configured must remain selectable
 * so the field can render its own value — an Autocomplete whose `value` is absent from `options`
 * drops the selection, which is the same class of silent loss this bug is about.
 *
 * The same predicate ("active OR already applied") governs the payer-category picker in SIMS-8634
 * and the cup picker in SIMS-8831. One rule, three screens.
 */
export function selectablePayerOptions<T extends RetainablePayer>(
	activePayers: T[],
	allPayers: T[],
	configuredPayerIds: string[] | undefined | null,
): T[] {
	const configured = new Set(configuredPayerIds ?? []);
	const options: T[] = [...activePayers];
	const seen = new Set(activePayers.map((payer) => payer._id));

	allPayers.forEach((payer) => {
		if (!seen.has(payer._id) && configured.has(payer._id)) {
			options.push(payer);
			seen.add(payer._id);
		}
	});

	return options;
}

/** True when a configured payer is being retained only because the program already uses it. */
export const isRetainedInactive = (payer: RetainablePayer): boolean =>
	payer.status !== ACTIVE_STATUS;
